import { useEffect, useState } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, Clock, AlertTriangle, Plus } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatsCard } from '@/components/ui/StatsCard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton, SkeletonStats, SkeletonTable } from '@/components/ui/Skeleton';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { formatRelative, formatDuration } from '@/lib/format';
import { CallSheet } from '@/components/sheets/CallSheet';
import { useVoiceStore } from '@/stores/voiceStore';
import { toast } from '@/stores/toastStore';
import type { VoiceCall } from '@nexus/shared/types';

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  completed: 'success',
  in_progress: 'info' as 'success',
  no_answer: 'warning',
  failed: 'error',
  queued: 'default',
  ringing: 'default',
};

const SENTIMENT_COLORS: Record<string, { label: string; color: string }> = {
  very_positive: { label: 'Very Positive', color: '#10b981' },
  positive: { label: 'Positive', color: '#34d399' },
  neutral: { label: 'Neutral', color: '#64748b' },
  negative: { label: 'Negative', color: '#f59e0b' },
  very_negative: { label: 'Very Negative', color: '#ef4444' },
};

export function VoiceOps() {
  const { calls, isLoading, error, fetch, create, clearError } = useVoiceStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNewCall, setShowNewCall] = useState(false);
  const [newCall, setNewCall] = useState({ phoneFrom: '', phoneTo: '', direction: 'outbound' as 'inbound' | 'outbound', contactId: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const completedCalls = calls.filter((c) => c.status === 'completed');
  const avgDuration =
    completedCalls.length > 0
      ? Math.round(completedCalls.reduce((sum, c) => sum + (c.duration ?? 0), 0) / completedCalls.length)
      : 0;
  const escalationRate =
    calls.length > 0
      ? ((calls.filter((c) => c.escalated).length / calls.length) * 100).toFixed(1)
      : '0.0';

  // Compute sentiment distribution from real calls
  const sentimentCounts: Record<string, number> = {};
  for (const call of calls) {
    const s = call.sentiment || 'neutral';
    sentimentCounts[s] = (sentimentCounts[s] || 0) + 1;
  }
  const sentimentDistribution = Object.entries(SENTIMENT_COLORS)
    .map(([key, cfg]) => ({
      name: cfg.label,
      value: sentimentCounts[key] || 0,
      color: cfg.color,
    }))
    .filter((d) => d.value > 0);

  const handleCreateCall = async () => {
    setCreating(true);
    try {
      await create({
        phoneFrom: newCall.phoneFrom,
        phoneTo: newCall.phoneTo,
        direction: newCall.direction,
        contactId: newCall.contactId || undefined,
      });
      toast.success('Call initiated');
      setShowNewCall(false);
      setNewCall({ phoneFrom: '', phoneTo: '', direction: 'outbound', contactId: '' });
    } catch {
      // error is set in store
    } finally {
      setCreating(false);
    }
  };

  const columns: Column<VoiceCall>[] = [
    {
      key: 'contact',
      header: 'Contact',
      sortable: true,
      render: (row) => (
        <span className="text-sm font-medium text-surface-200">
          {row.direction === 'inbound' ? row.phoneFrom : row.phoneTo}
        </span>
      ),
    },
    {
      key: 'direction',
      header: 'Direction',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          {row.direction === 'inbound' ? (
            <PhoneIncoming className="h-3.5 w-3.5 text-blue-400" />
          ) : (
            <PhoneOutgoing className="h-3.5 w-3.5 text-emerald-400" />
          )}
          <span className="text-xs text-surface-400 capitalize">{row.direction}</span>
        </div>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-surface-300">
          {row.duration && row.duration > 0 ? formatDuration(row.duration) : '--'}
        </span>
      ),
    },
    {
      key: 'sentiment',
      header: 'Sentiment',
      render: (row) => <SentimentIndicator sentiment={row.sentiment || 'neutral'} showLabel />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge variant={statusVariant[row.status] || 'default'}>{row.status.replace('_', ' ')}</Badge>,
    },
    {
      key: 'escalated',
      header: 'Escalated',
      render: (row) =>
        row.escalated ? (
          <Badge variant="error">Yes</Badge>
        ) : (
          <span className="text-xs text-surface-500">No</span>
        ),
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (row) => <span className="text-xs text-surface-400">{formatRelative(row.createdAt)}</span>,
    },
  ];

  if (isLoading && calls.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-40 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-9 w-28 rounded-lg" />
        </div>
        {/* Stats — 4 cards */}
        <SkeletonStats count={4} />
        {/* Main grid: table 2/3 + chart 1/3 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonTable rows={6} cols={7} />
          </div>
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <Skeleton className="h-4 w-28 mb-4" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Voice Operations</h2>
          <p className="text-sm text-surface-400 mt-0.5">Call management, transcription, and sentiment analysis</p>
        </div>
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setShowNewCall(true)}>
          New Call
        </Button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 cursor-pointer">Dismiss</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard label="Total Calls" value={calls.length} icon={<Phone className="h-5 w-5" />} />
        <StatsCard label="Avg. Duration" value={formatDuration(avgDuration)} icon={<Clock className="h-5 w-5" />} />
        <StatsCard label="Escalation Rate" value={`${escalationRate}%`} icon={<AlertTriangle className="h-5 w-5" />} />
        <StatsCard label="Completed" value={completedCalls.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calls table */}
        <div className="lg:col-span-2">
          <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
            <DataTable columns={columns} data={calls} onRowClick={(row) => setSelectedId(row.id)} />
          </div>
        </div>

        {/* Sentiment chart */}
        <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-surface-200 mb-4">Call Sentiment</h3>
          <div className="h-64">
            {sentimentDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sentimentDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {sentimentDistribution.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#f1f5f9',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-surface-500">No sentiment data yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Call Modal */}
      <Modal open={showNewCall} onClose={() => setShowNewCall(false)} title="New Call">
        <div className="space-y-4">
          <Input
            label="From Number"
            placeholder="+1 555-000-0000"
            value={newCall.phoneFrom}
            onChange={(e) => setNewCall((p) => ({ ...p, phoneFrom: e.target.value }))}
          />
          <Input
            label="To Number"
            placeholder="+1 555-000-0000"
            value={newCall.phoneTo}
            onChange={(e) => setNewCall((p) => ({ ...p, phoneTo: e.target.value }))}
          />
          <Select
            label="Direction"
            options={[
              { value: 'outbound', label: 'Outbound' },
              { value: 'inbound', label: 'Inbound' },
            ]}
            value={newCall.direction}
            onChange={(e) => setNewCall((p) => ({ ...p, direction: e.target.value as 'inbound' | 'outbound' }))}
          />
          <Input
            label="Contact ID (optional)"
            placeholder="ct_..."
            value={newCall.contactId}
            onChange={(e) => setNewCall((p) => ({ ...p, contactId: e.target.value }))}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowNewCall(false)}>Cancel</Button>
            <Button
              loading={creating}
              disabled={!newCall.phoneFrom || !newCall.phoneTo}
              onClick={handleCreateCall}
            >
              Create Call
            </Button>
          </div>
        </div>
      </Modal>

      {/* Call Detail Sheet */}
      <CallSheet
        id={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
